
import { useToast } from '@/hooks/use-toast';
import { AdminService } from '@/services/adminService';
import { supabase } from '@/integrations/supabase/client';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';

interface IdealProfile {
  id: string;
  name: string;
  dominance_min: number;
  dominance_max: number;
  extraversion_min: number;
  extraversion_max: number;
  patience_min: number;
  patience_max: number;
  formality_min: number;
  formality_max: number;
  objectivity_min: number;
  objectivity_max: number;
  created_at: string;
  updated_at: string;
}

export const useAdminActions = () => {
  const { toast } = useToast();
  const { user, session, isAdmin } = useAuth();
  const { currentCompany } = useCompany();
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const logSupabaseError = (label: string, error: any) => {
    // Supabase/PostgREST error objects vary; log as much as possible
    console.error(label, {
      message: error?.message,
      details: error?.details,
      hint: error?.hint,
      code: error?.code,
      status: error?.status,
      raw: error
    });
  };

  const handleDeleteIdealProfile = async (
    profile: IdealProfile,
    removeOptimistically: (profileId: string) => void,
    restoreProfile: (profile: IdealProfile) => void,
    setDeleting: (profileId: string, isDeleting: boolean) => void
  ): Promise<boolean> => {
    if (!confirm('Are you sure you want to delete this ideal profile?')) {
      return false;
    }

    // Immediately remove from UI
    removeOptimistically(profile.id);
    setDeleting(profile.id, true);

    try {
      // Perform actual deletion
      await AdminService.deleteIdealProfile(profile.id);
      
      console.log('Profile deleted successfully:', profile.id);
      
      return true;
    } catch (error: any) {
      console.error('Error deleting ideal profile:', error);
      
      // Restore the profile since deletion failed
      restoreProfile(profile);
      
      toast({
        title: "Error",
        description: "Failed to delete ideal profile",
        variant: "destructive"
      });
      
      return false;
    } finally {
      setDeleting(profile.id, false);
    }
  };

  /**
   * Unlinks a candidate from the company by setting company_id to null.
   * This preserves the assessment data while removing it from the company's view.
   */
  const handleSubmissionDelete = async (
    submissionId: string,
    onSuccess: () => void,
    isCognitiveOnly?: boolean,
    submissionEmail?: string,
    submissionFullName?: string
  ): Promise<boolean> => {
    console.log('Starting unlinking for submission:', submissionId);
    console.log('Is cognitive-only:', isCognitiveOnly);
    console.log('Current user:', user?.id);
    console.log('Current company:', currentCompany?.company_id);
    
    // Check authentication first
    if (!user || !session) {
      console.error('User not authenticated');
      toast({
        title: "Authentication Required",
        description: "You must be logged in to remove candidates. Please refresh the page and try again.",
        variant: "destructive"
      });
      return false;
    }

    // Verify we have a company context
    if (!currentCompany?.company_id) {
      console.error('No company context available');
      toast({
        title: "Company Required",
        description: "Unable to determine your company. Please refresh the page.",
        variant: "destructive"
      });
      return false;
    }

    // Check session validity
    const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !currentSession) {
      console.error('Session validation failed:', sessionError);
      toast({
        title: "Session Expired",
        description: "Your session has expired. Please log out and log back in.",
        variant: "destructive"
      });
      return false;
    }

    const companyId = currentCompany.company_id;

    // Debug: confirm membership and target row context
    try {
      const { data: membershipRows, error: membershipErr } = await supabase
        .from('user_company_associations')
        .select('company_id, role, is_active')
        .eq('user_id', user.id)
        .eq('company_id', companyId)
        .limit(5);

      if (membershipErr) {
        logSupabaseError('[unlink debug] membership query failed', membershipErr);
      } else {
        console.log('[unlink debug] membership rows for active company:', membershipRows);
      }
    } catch (e) {
      console.error('[unlink debug] membership query threw', e);
    }

    try {
      // Get the email and full_name - either passed directly or we need to fetch them
      let email = submissionEmail;
      let fullName = submissionFullName;
      
      if (!email || !fullName) {
        if (isCognitiveOnly) {
          // Fetch from cognitive_submissions for cognitive-only candidates
          const { data: cogRow, error: fetchError } = await supabase
            .from('cognitive_submissions')
            .select('email, full_name')
            .eq('id', submissionId)
            .maybeSingle();
          
          if (fetchError) {
            logSupabaseError('Error fetching cognitive submission details', fetchError);
          }
          email = email || cogRow?.email;
          fullName = fullName || cogRow?.full_name;
        } else {
          // Fetch email and full_name from survey_submissions if not provided
          const { data: surveyRow, error: fetchError } = await supabase
            .from('survey_submissions')
            .select('email, full_name')
            .eq('id', submissionId)
            .maybeSingle();
          
          if (fetchError) {
            logSupabaseError('Error fetching submission details', fetchError);
          }
          email = email || surveyRow?.email;
          fullName = fullName || surveyRow?.full_name;
        }
      }
      
      if (!email || !fullName) {
        console.error('Email and full_name required for unlinking candidate');
        toast({
          title: "Error",
          description: "Unable to identify the candidate to remove.",
          variant: "destructive"
        });
        return false;
      }

      console.log('Unlinking candidate by email:', email, 'and full_name:', fullName, 'from company:', companyId);

      console.log('Unlinking candidate by email:', email, 'from company:', companyId);

      // Step 1: Soft delete from survey_submissions (behavioral & work behavior)
      // Match by both email AND full_name to handle cases where multiple candidates share the same email
      const { data: surveyRows, error: surveyFetchError } = await supabase
        .from('survey_submissions')
        .select('id')
        .eq('email', email)
        .eq('full_name', fullName)
        .eq('company_id', companyId)
        .is('deleted_at', null);

      if (surveyFetchError) {
        logSupabaseError('Error fetching survey submissions', surveyFetchError);
      } else if (surveyRows && surveyRows.length > 0) {
        console.log('Found survey submissions to soft delete:', surveyRows.length);
        
        const { error: surveyUpdateError } = await supabase
          .from('survey_submissions')
          .update({ deleted_at: new Date().toISOString() })
          .eq('email', email)
          .eq('full_name', fullName)
          .eq('company_id', companyId)
          .is('deleted_at', null);

        if (surveyUpdateError) {
          logSupabaseError('Error soft deleting survey submissions', surveyUpdateError);
          throw new Error(`Failed to remove from survey_submissions: ${surveyUpdateError.message}`);
        }
        console.log('Survey submissions soft deleted successfully');
      } else {
        console.log('No survey submissions found for this email+full_name/company');
      }

      // Step 2: Soft delete from cognitive_submissions
      // Match by both email AND full_name to handle cases where multiple candidates share the same email
      const { data: cognitiveRows, error: cognitiveFetchError } = await supabase
        .from('cognitive_submissions')
        .select('id')
        .eq('email', email)
        .eq('full_name', fullName)
        .eq('company_id', companyId)
        .is('deleted_at', null);

      if (cognitiveFetchError) {
        logSupabaseError('Error fetching cognitive submissions', cognitiveFetchError);
      } else if (cognitiveRows && cognitiveRows.length > 0) {
        console.log('Found cognitive submissions to soft delete:', cognitiveRows.length);
        
        const { error: cognitiveUpdateError } = await supabase
          .from('cognitive_submissions')
          .update({ deleted_at: new Date().toISOString() })
          .eq('email', email)
          .eq('full_name', fullName)
          .eq('company_id', companyId)
          .is('deleted_at', null);

        if (cognitiveUpdateError) {
          logSupabaseError('Error soft deleting cognitive submissions', cognitiveUpdateError);
          throw new Error(`Failed to remove from cognitive_submissions: ${cognitiveUpdateError.message}`);
        }
        console.log('Cognitive submissions soft deleted successfully');
      } else {
        console.log('No cognitive submissions found for this email+full_name/company');
      }

      // Call success callback to update UI
      onSuccess();
      
      toast({
        title: "Candidate Removed",
        description: "The candidate has been removed from your company's view.",
      });
      
      return true;
    } catch (error: any) {
      console.error('Error in unlinking:', error);
      
      toast({
        title: "Error",
        description: `Failed to remove candidate: ${error.message || 'Unknown error'}`,
        variant: "destructive"
      });
      
      return false;
    }
  };

  const handleSubmissionDeleted = async (
    submissionId: string,
    onSuccess: () => void,
    isCognitiveOnly?: boolean,
    submissionEmail?: string,
    submissionFullName?: string
  ) => {
    return await handleSubmissionDelete(submissionId, onSuccess, isCognitiveOnly, submissionEmail, submissionFullName);
  };

  const handleViewDetails = (submission: any) => {
    console.log('View details for:', submission);
    setSelectedSubmission(submission);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSubmission(null);
  };

  return {
    handleDeleteIdealProfile,
    handleSubmissionDelete,
    handleSubmissionDeleted,
    handleViewDetails,
    handleCloseModal,
    selectedSubmission,
    isModalOpen
  };
};
